/**
 * @voltxampere/engine — ERC rule: saturation (Sprint 56)
 *
 * Active devices (BJT, MOSFET) stuck in deep saturation at DC OP
 * are usually a circuit-design mistake rather than an intent — the
 * device is acting as a switch rather than an amplifier, or the
 * bias network is wrong.
 *
 * Detection heuristic (S56a MVP — voltage-based proxy):
 *   - BJT NPN: V_CE < 0.3 V (saturation threshold, Ebers-Moll knee).
 *   - BJT PNP: V_EC < 0.3 V.
 *   - MOSFET NMOS: V_DS < V_GS - V_TH and V_DS > 0 (triode region).
 *   - MOSFET PMOS: V_SD < V_SG - |V_TH| and V_SD > 0.
 *
 * Reads node voltages from the DC OP result. Components whose
 * required terminal nets aren't in the dcResult silently skip (e.g.
 * a partial DC solve, or component types this rule doesn't cover).
 *
 * Sprint 57+ may extend with current-based detection for tighter
 * accuracy (saturation is technically defined by collector current
 * crossing the boundary, not V_CE alone).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const VCE_SAT_THRESHOLD = 0.3; // V
export const saturationRule = {
    id: "saturation",
    defaultSeverity: "warn",
    requiresDCResult: true,
    check(circuit, dcResult) {
        if (dcResult === undefined)
            return [];
        const violations = [];
        function readV(netId) {
            if (netId === undefined)
                return null;
            const v = dcResult?.nodeVoltages.get(netId);
            return typeof v === "number" ? v : null;
        }
        for (const c of circuit.components) {
            if (c.type === "bjt-npn" || c.type === "bjt-pnp") {
                const collectorNet = c.terminals.find((t) => t.terminalId === "collector")?.netId;
                const emitterNet = c.terminals.find((t) => t.terminalId === "emitter")?.netId;
                const vC = readV(collectorNet);
                const vE = readV(emitterNet);
                if (vC === null || vE === null)
                    continue;
                const vCE = c.type === "bjt-npn" ? vC - vE : vE - vC;
                if (vCE >= 0 && vCE < VCE_SAT_THRESHOLD) {
                    violations.push({
                        ruleId: "saturation",
                        severity: "warn",
                        i18nKey: "erc.saturation.message",
                        suggestionKey: "erc.saturation.suggestion",
                        meta: {
                            componentId: c.id,
                            componentType: c.type,
                            vCE,
                            threshold: VCE_SAT_THRESHOLD,
                        },
                    });
                }
            }
            // MOSFET triode-region detection — V_DS < V_GS - V_TH proxy.
            if (c.type === "mosfet-nmos" || c.type === "mosfet-pmos") {
                const drainNet = c.terminals.find((t) => t.terminalId === "drain")?.netId;
                const sourceNet = c.terminals.find((t) => t.terminalId === "source")?.netId;
                const gateNet = c.terminals.find((t) => t.terminalId === "gate")?.netId;
                const vD = readV(drainNet);
                const vS = readV(sourceNet);
                const vG = readV(gateNet);
                if (vD === null || vS === null || vG === null)
                    continue;
                const params = c.params;
                const vt0Raw = params.Vt0;
                const vTh = typeof vt0Raw === "number" ? Math.abs(vt0Raw) : 0.7;
                if (c.type === "mosfet-nmos") {
                    const vGS = vG - vS;
                    const vDS = vD - vS;
                    if (vGS > vTh && vDS > 0 && vDS < vGS - vTh) {
                        violations.push({
                            ruleId: "saturation",
                            severity: "warn",
                            i18nKey: "erc.saturation.message",
                            suggestionKey: "erc.saturation.suggestion",
                            meta: {
                                componentId: c.id,
                                componentType: c.type,
                                vDS,
                                vGSMinusVTH: vGS - vTh,
                            },
                        });
                    }
                }
                else {
                    const vSG = vS - vG;
                    const vSD = vS - vD;
                    if (vSG > vTh && vSD > 0 && vSD < vSG - vTh) {
                        violations.push({
                            ruleId: "saturation",
                            severity: "warn",
                            i18nKey: "erc.saturation.message",
                            suggestionKey: "erc.saturation.suggestion",
                            meta: {
                                componentId: c.id,
                                componentType: c.type,
                                vSD,
                                vSGMinusVTH: vSG - vTh,
                            },
                        });
                    }
                }
            }
        }
        return violations;
    },
};
//# sourceMappingURL=saturation.js.map